// features/join/JoinPage.tsx — `/join/<code>`, where a personal invite link lands (1.24.00 B2).
//
// Public (BareLayout, no auth gate — the whole point is that the visitor has no
// account). Three states:
//   • signed out, valid code   → "@handle invited you to TruXSocial" + Create account
//                                (carries the code into /signup?invite=<code>)
//   • signed in, valid code    → "You're already here — follow @handle instead" + Follow
//   • unknown / revoked code   → the honest "This invite link isn't valid anymore" — a
//                                real 404, never a fake inviter. Sign-up is still one
//                                tap away, without the code.
// The server-rendered meta view answers crawlers for the same URL; this is the
// in-app page a human sees.

import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Button, Avatar } from '@/ui';
import { Icon } from '@/icons';
import {
  useSession,
  useInviteInfo,
  useFollow,
  useRequireAuth,
  followButtonLabel,
  followButtonVariant,
  usePageTitle,
} from '@/hooks';

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const Card = styled(Panel).attrs({ $brackets: false })`
  max-width: 460px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px;
  text-align: center;
  align-items: center;
`;

const Brand = styled.div`
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 800;
  color: ${({ theme }) => theme.color.textPrimary};
  b { color: ${({ theme }) => theme.color.safety}; }
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.3;
`;

const Body = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.55;
`;

const Inviter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Name = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Verified = styled.span`
  display: inline-flex;
  line-height: 0;
  color: ${({ theme }) => theme.color.accentCyan};
`;

const Cta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  padding: 10px 18px;
  border-radius: ${({ theme }) => theme.radius.md};
  text-decoration: none;
  color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.accentCyan};
`;

const Quiet = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.accentCyan};
  text-decoration: none;
`;

const Rules = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

function FollowInviter({ handle }: { handle: string }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(handle);
  const requireAuth = useRequireAuth();
  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }
  return (
    <Button
      variant={followButtonVariant(isFollowing, isRequested)}
      onClick={handleFollow}
      aria-label={`${followButtonLabel(isFollowing, isRequested)} ${handle}`}
    >
      {followButtonLabel(isFollowing, isRequested)}
    </Button>
  );
}

export function JoinPage() {
  const { code } = useParams();
  const { isAuthenticated } = useSession();
  const { info, loading, error } = useInviteInfo(code);
  usePageTitle(
    info ? `${info.inviter.handle} invited you / TruXSocial` : 'Join TruXSocial',
  );

  if (loading) {
    return (
      <Wrap>
        <Card>
          <Brand>Tru<b>X</b>Social</Brand>
          <Title>Opening your invite…</Title>
          <Body role="status">One moment.</Body>
        </Card>
      </Wrap>
    );
  }

  if (error || !info) {
    return (
      <Wrap>
        <Card>
          <Brand>Tru<b>X</b>Social</Brand>
          <Title>This invite link isn&apos;t valid anymore</Title>
          <Body>
            {error === 'not_found'
              ? 'The person who shared it may have made a new one. You can still join without it.'
              : 'We could not check it just now. You can still join without it.'}
          </Body>
          {isAuthenticated ? (
            <Quiet to="/">Back to your feed</Quiet>
          ) : (
            <Cta to="/signup">Create an account</Cta>
          )}
        </Card>
      </Wrap>
    );
  }

  const { inviter } = info;
  const signupHref = `/signup?invite=${encodeURIComponent(code ?? '')}`;

  return (
    <Wrap>
      <Card>
        <Brand>Tru<b>X</b>Social</Brand>
        <Inviter>
          <Avatar name={inviter.name} size={64} avatarKey={inviter.av} />
          <Name>
            {inviter.name}
            {inviter.verified && (
              <Verified aria-label="Verified" role="img">
                <Icon name="verify" size={16} />
              </Verified>
            )}
          </Name>
          <Handle>{inviter.handle}</Handle>
        </Inviter>

        {isAuthenticated ? (
          <>
            <Title>You&apos;re already here. Follow {inviter.handle} instead</Title>
            <Body>This link is for people who don&apos;t have an account yet.</Body>
            <FollowInviter handle={inviter.handle} />
            <Quiet to="/">Back to your feed</Quiet>
          </>
        ) : (
          <>
            <Title>{inviter.handle} invited you to TruXSocial</Title>
            <Body>Free speech, five rules, no algorithmic gaming. Come say something.</Body>
            <Cta to={signupHref}>
              <Icon name="user-plus" size={16} /> Create an account
            </Cta>
            <Quiet to="/signin">Already have one? Sign in</Quiet>
            <Rules>
              The only things that get moderated: direct threats, terrorism, scams, fraud,
              coordinated abuse.
            </Rules>
          </>
        )}
      </Card>
    </Wrap>
  );
}
